/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from 'react';

import {
  Badge,
  Button,
  Form,
  message,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';

import {
  Company,
  CompanyType,
  LicenseStatus,
  LicenseType,
} from '../../../types';
import { deleteCompany } from '../../../utils/httpRequest';
import Page from '../../../components/page';
import ActionHeader from '../../../components/actionHeader';
import { Content } from './styles';
import { SVGDelete, SVGEdit } from '../../../images';
import Icon, { PlusCircleOutlined } from '@ant-design/icons';
import CreateTrialLicenseModal from '../components/createLicenseModal';
import { useNavigate } from 'react-router';
import CreateClientModal from './createClientModal';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import {
  fetchCompanies,
  selectCompanies,
  selectIsFetchingCompany,
  setCompanies,
} from '../../../slices/companies';
import { useAppDispatch } from '../../../store';
import { useTranslation } from 'react-i18next';

const ClientList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const companies = useSelector(selectCompanies);
  const [loading, setLoading] = useState(false);
  const [editingCompanyID, setEditingCompanyID] = useState('');
  const [messageApi, contextHolder] = message.useMessage();
  const [showCreateTrialLicenseModal, setShowCreateTrialLicenseModal] =
    useState(false);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const currentCompany = useRef<Company>(null);

  const isFetchingCompany = useSelector(selectIsFetchingCompany);

  const editingCompany = companies.find(
    (i) => i.companyID === editingCompanyID
  );

  const handleAddCompany = () => {
    setEditingCompanyID('');
    setShowCreateClientModal(true);
  };

  const handleEditCompany = async (record: Company) => {
    setEditingCompanyID(record.companyID);
    setShowCreateClientModal(true);
  };

  const handleDeleteCompany = async (id: string) => {
    try {
      setLoading(true);
      await deleteCompany(id);
      const nextCompanies = companies.filter((i) => i.companyID !== id);
      dispatch(setCompanies(nextCompanies));
    } catch (err: any) {
      messageApi.error('Delete failed!');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLicenseClose = () => {
    currentCompany.current = null;
    setShowCreateTrialLicenseModal(false);
  };

  const handleCreateLicense = (company: Company) => {
    currentCompany.current = company;
    setShowCreateTrialLicenseModal(true);
  };

  const getData = async () => {
    dispatch(fetchCompanies());
  };

  useEffect(() => {
    getData();
  }, []);

  const columns = [
    {
      title: t('company_name'),
      dataIndex: 'name',
      width: 200,
      fixed: 'left' as const,
    },
    {
      title: t('license_status'),
      dataIndex: 'licenseType',
      render: (value: string, record: Company) => {
        if (record.licenseStatus === LicenseStatus.INACTIVE) {
          return (
            <>
              <Tag
                color="red"
                onClick={() => navigate(`${record.companyID}/license`)}
              >
                Inactive
              </Tag>
              <br />
              <Typography.Link onClick={() => handleCreateLicense(record)}>
                <PlusCircleOutlined /> Create License
              </Typography.Link>
            </>
          );
        }

        if (record.licenseStatus === LicenseStatus.ACTIVE) {
          return record.licenseType === LicenseType.PRODUCTION ? (
            <Tag
              color="green"
              style={{ cursor: 'pointer ' }}
              onClick={() => navigate(`${record.companyID}/license`)}
            >
              <Space>
                <Badge color="green" />
                Production
              </Space>
            </Tag>
          ) : (
            <Tag
              color="orange"
              style={{ cursor: 'pointer ' }}
              onClick={() => navigate(`${record.companyID}/license`)}
            >
              <Space>
                <Badge color="orange" />
                Trial
              </Space>
            </Tag>
          );
        }

        return (
          <Typography.Link onClick={() => handleCreateLicense(record)}>
            <PlusCircleOutlined /> Create License
          </Typography.Link>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      options: [
        {
          label: CompanyType.SOLVV,
          value: CompanyType.SOLVV,
        },
        {
          label: CompanyType.DEMO,
          value: CompanyType.DEMO,
        },
        {
          label: CompanyType.REAL,
          value: CompanyType.REAL,
        },
      ],
    },
    {
      title: t('email'),
      dataIndex: 'email',
      render: (value: any) => value || '-',
    },
    {
      title: t('address'),
      dataIndex: 'address',
      render: (value: any) => value || '-',
    },
    {
      title: t('contact'),
      dataIndex: 'contact',
      render: (value: any) => value || '-',
    },
    {
      title: t('description'),
      dataIndex: 'description',
      render: (value: any) => value || '-',
    },
    {
      title: t('action'),
      dataIndex: 'action',
      width: 100,
      fixed: 'right' as const,
      render: (value: any, record: Company, index: number) => {
        return (
          <Space>
            <Typography.Link onClick={() => handleEditCompany(record)}>
              <Icon component={SVGEdit} /> Edit
            </Typography.Link>
            <Popconfirm
              placement="bottom"
              title={`Are you sure you want to delete ${record.name}?`}
              okText={t('yes')}
              cancelText={t('no')}
              onConfirm={() => handleDeleteCompany(record.companyID)}
            >
              <Typography.Link type="danger" title={t('delete')}>
                <Icon component={SVGDelete} />
              </Typography.Link>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Page
      header={
        <ActionHeader
          title={t('client_management')}
          titleTag={companies.length ? `(${companies.length})` : ''}
          endSlot={
            <Button type="primary" onClick={handleAddCompany}>
              {t('add_new_client')}
            </Button>
          }
        />
      }
    >
      <Content>
        {contextHolder}
        <Form form={form} component={false}>
          <Table
            columns={columns}
            dataSource={companies.map((i) => ({ key: i.companyID, ...i }))}
            loading={loading || isFetchingCompany}
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 8 }}
          ></Table>
        </Form>
        <CreateTrialLicenseModal
          open={showCreateTrialLicenseModal}
          company={currentCompany.current as Company}
          onClose={() => {
            handleCreateLicenseClose();
          }}
          onConfirm={() => {
            handleCreateLicenseClose();
            getData();
          }}
        />
        <CreateClientModal
          open={showCreateClientModal}
          onClose={() => setShowCreateClientModal(false)}
          onConfirm={() => {
            setShowCreateClientModal(false);
            getData();
            setEditingCompanyID('');
          }}
          isEdit={!!editingCompanyID}
          company={editingCompany}
        />
      </Content>
    </Page>
  );
};

export default ClientList;
